import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Building2, Dumbbell, Coffee } from 'lucide-react';
import { useCases } from '../data/mock';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const iconMap = {
  Home, Building2, Dumbbell, Coffee
};

const UseCases = () => {
  const handleContactUs = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Smart RO for Every Need</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            From homes to commercial spaces, discover how Niloman HX transforms water purification across diverse settings
          </p>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {useCases.map((useCase, index) => {
              const Icon = iconMap[useCase.icon];
              const isEven = index % 2 === 0;

              return (
                <div 
                  key={useCase.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative h-[400px] rounded-3xl overflow-hidden shadow-2xl">
                      <img 
                        src={useCase.image}
                        alt={useCase.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-3">
                          <Icon className="h-7 w-7 text-cyan-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''} space-y-6`}>
                    <div>
                      <div className="inline-flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-4xl font-bold text-gray-900">
                          {useCase.title}
                        </h2>
                      </div>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>

                    <Card className="border-2 border-cyan-200 bg-white shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-xl text-gray-900">Key Benefits</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {useCase.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <div className="w-2 h-2 bg-cyan-600 rounded-full"></div>
                              </div>
                              <span className="text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted Across India
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of satisfied customers enjoying smart water purification
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  500+
                </CardTitle>
                <CardDescription className="text-base font-medium text-gray-700">
                  Homes Protected
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  100+
                </CardTitle>
                <CardDescription className="text-base font-medium text-gray-700">
                  Commercial Clients
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  99.9%
                </CardTitle>
                <CardDescription className="text-base font-medium text-gray-700">
                  System Uptime
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  24/7
                </CardTitle>
                <CardDescription className="text-base font-medium text-gray-700">
                  Monitoring Support
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Water Quality?
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            Let's discuss how Niloman HX can meet your specific water purification needs
          </p>
          <Button 
            onClick={handleContactUs}
            size="lg" 
            className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8"
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
